import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import { badgeRecipe, type BadgeRecipeProps } from '../recipes/badge.recipe';
import { toneForStatus } from './status-tone';
import { sentenceCase } from './call-status';

/** Where a job has got to. A different axis from severity — see badge.recipe. */
export type JobPhase =
  | 'dispatched'
  | 'en-route'
  | 'on-scene'
  | 'towing'
  | 'returning';

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    BadgeRecipeProps {
  /**
   * Colour by job phase instead of severity. Takes precedence over `tone`;
   * setting both is a mistake, since a badge only has one colour.
   */
  phase?: JobPhase;
  /** Icon before the label. Sized automatically. */
  icon?: React.ReactNode;
}

/**
 * A short status marker.
 *
 * `tone` carries the meaning and `appearance` how loudly to say it. Default is
 * subtle, because a screen full of solid badges stops communicating anything.
 *
 * Not interactive — if it needs to be clickable, it is a Button.
 *
 * The dashboard's status pill, sized for dense tables; the phone's is
 * StatusChip. Neither draws a dot: the fill is the colour.
 */
export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  function Badge(
    {
      className,
      tone,
      phase,
      appearance,
      size,
      wrap,
      icon,
      children,
      ...props
    },
    ref
  ) {
    // One colour axis underneath. The prefix is an implementation detail of the
    // recipe, so call sites write phase="towing" rather than the full value.
    const resolved = phase ? (`phase-${phase}` as const) : tone;

    return (
      <span
        ref={ref}
        className={cn(
          badgeRecipe({ tone: resolved, appearance, size, wrap }),
          className
        )}
        {...props}
      >
        {icon}
        {children}
      </span>
    );
  }
);

export interface StatusBadgeProps
  extends Omit<BadgeProps, 'tone' | 'children'> {
  /** A status string from the API. Matched case-insensitively. */
  status: string;
  /** Override the label; defaults to the status prettified. */
  label?: React.ReactNode;
}

/**
 * A Badge whose colour is derived from a status string, using the shared
 * `statusTone` map. Unknown statuses fall back to neutral rather than throwing,
 * so a new value from the API degrades quietly instead of breaking a page.
 */
export const StatusBadge = React.forwardRef<HTMLSpanElement, StatusBadgeProps>(
  function StatusBadge({ status, label, ...props }, ref) {
    const tone = toneForStatus(status);
    const text = label ?? (status ? sentenceCase(status) : status);

    return (
      <Badge ref={ref} tone={tone} {...props}>
        {text}
      </Badge>
    );
  }
);
